import { defineComponent, h, withDirectives, resolveDirective } from 'vue';
import { QSelect, QIcon, QImg, QItem, QItemSection, QItemLabel } from 'quasar';
import BaseComponent from './baseFormMixin.jsx';
import variableBindingMixin from './variableBindingMixin.jsx';
import utils from '../../../Shared/utils.jsx';
import methods from '../../../Shared/methods';
import careHelpfulFunctions from '../../careHelpfulFunctions.jsx';
import { appSettings } from '@/Shared/appSettings.js';

import TranslationContainer from '../TranslationContainer.jsx';

export default defineComponent({
    name: 'sform-basiciconpicker',
    mixins: [BaseComponent, variableBindingMixin],
    components: {
        QSelect,
        QIcon,
        QImg,
        QItem,
        QItemSection,
        QItemLabel,
        TranslationContainer,
    },
    data: () => ({
        formatData: null,

        dolookupexpn: null,
        lookupurlexpn: null,
        lookuprawexpn: null,

        displayexpnfunc: null,
        valueexpnfunc: null,
        iconexpnfunc: null,

        item_text: 'name',
        item_value: 'value',
        item_disabled: undefined,
        item_icon: null,

        lookupitems: null,
        filter: '',
        customInputText: '',
        isCustomInputEditing: false,
        customInputDirty: false,
    }),
    props: {
    },
    created() {
        if (this.element.formatData)
            this.formatData = this.element.formatData;
        else if (this.element.schema && this.element.schema.formatData)
            this.formatData = this.element.schema.formatData;

        if (this.formatData && (this.formatData.LookupType || this.formatData.LookupURL || this.formatData.LookupInterpolatedExpression)) {
            if (this.formatData.LookupExpression)
                this.dolookupexpn = utils.compileExpression(this, this.formatData.LookupExpression);

            if ((!this.formatData.LookupType || this.formatData.LookupType == 'URL') && this.formatData.LookupURL)
                this.lookupurlexpn = utils.compile(this, this.formatData.LookupURL);
            else if (this.formatData.LookupType == 'Interpolated' && this.formatData.LookupInterpolatedExpression)
                this.lookuprawexpn = utils.compileExpression(this, this.formatData.LookupInterpolatedExpression);

            if (this.formatData.DisplayType == 'Expression') {
                const expr = utils.parseExpressionWithFilters(this.formatData.DisplayExpression);
                this.displayexpnfunc = {
                    code: expr,
                    func: new Function('control', 'util', this.formatData.LookupModelAs, `with (control) return ${expr};`)
                };
            }
            if (this.formatData.ValueType == 'Expression') {
                const expr = utils.parseExpressionWithFilters(this.formatData.ValueExpression);
                this.valueexpnfunc = {
                    code: expr,
                    func: new Function('control', 'util', this.formatData.LookupModelAs, `with (control) return ${expr};`)
                };
            }
            if (this.formatData.IconUrlType == 'Expression') {
                const expr = utils.parseExpressionWithFilters(this.formatData.IconUrlExpression);
                this.iconexpnfunc = {
                    code: expr,
                    func: new Function('control', 'util', this.formatData.LookupModelAs, `with (control) return ${expr};`)
                };
            }

            switch (this.formatData.DisplayType) {
                case 'Fieldname': this.item_text = this.formatData.DisplayField; break;
                case 'Expression':
                    this.item_text = (data) => {
                        try {
                            return this.displayexpnfunc.func(this, careHelpfulFunctions, data);
                        }
                        catch (e) {
                            utils.warn(`LookupList DisplayType Expression failed to evaluate: ${this.displayexpnfunc.code}; original: ${this.formatData.DisplayExpression}`, e);
                            return '';
                        }
                    };
                    break;
            }
            switch (this.formatData.ValueType) {
                case 'Fieldname': this.item_value = this.formatData.ValueField; break;
                case 'Expression':
                    this.item_value = (data) => {
                        try {
                            return this.valueexpnfunc.func(this, careHelpfulFunctions, data);
                        }
                        catch (e) {
                            utils.warn(`LookupList ValueType Expression failed to evaluate: ${this.valueexpnfunc.code}; original: ${this.formatData.ValueExpression}`, e);
                            return '';
                        }
                    };
                    break;
            }
            switch (this.formatData.IconUrlType) {
                case 'Fieldname': this.item_icon = this.formatData.IconUrlField; break;
                case 'Expression':
                    this.item_icon = (data) => {
                        try {
                            return this.iconexpnfunc.func(this, careHelpfulFunctions, data);
                        }
                        catch (e) {
                            utils.warn(`formBasicIconPicker IconUrlType Expression failed to evaluate: ${this.iconexpnfunc.code}; original: ${this.formatData.IconUrlExpression}`, e);
                            return '';
                        }
                    };
                    break;
            }
        }
    },
    async mounted() {
        await this.Refresh();
        this.loadComplete();
    },
    computed: {
        tolookup: function () {
            if (this.dolookupexpn)
                return utils.evaluate(this.dolookupexpn, this);
            else
                return true;
        },
        lookupurl: function () {
            if (this.lookupurlexpn)
                try {
                    return utils.evaluate(this.lookupurlexpn, this, false, null, true);
                }
                catch (e) {
                    utils.warn(`basiciconpicker lookupurl could not be evaluated: ${this.lookupurlexpn.code}`, e);
                    return '';
                }
            else
                return '';
        },
        lookupraw: function () {
            if (this.lookuprawexpn)
                try {
                    return utils.evaluate(this.lookuprawexpn, this, false, null, true);
                }
                catch (e) {
                    utils.warn(`basiciconpicker lookupraw could not be evaluated: ${this.lookuprawexpn.code}`, e);
                    return [];
                }
            else
                return '';
        },
        filteredItems() {
            const items = this.lookupitems || [];
            if (!this.filter)
                return items;
            const needle = this.filter.toLowerCase();
            return items.filter(item => this.getItemLabel(item).toLowerCase().includes(needle));
        },
    },
    watch: {
        lookupurl: function () {
            this.Refresh();
        },
        lookupraw: function () {
            this.Refresh();
        },
    },
    methods: {
        async Refresh() {
            if (this.tolookup) {
                if (this.lookupurlexpn) {
                    let url = this.lookupurl;
                    if (url)
                        try {
                            this.lookupitems = await utils.api.get(url, false, this.formatData.CacheLookupResult);
                        }
                        catch (e) {
                            utils.warn(`Failed in form basiciconpicker lookupurl:${url}`, e);
                            this.lookupitems = [];
                        }
                }
                else if (this.lookuprawexpn) {
                    this.lookupitems = this.lookupraw;
                }
            }
        },
        getItemLabel(item) {
            const raw = typeof this.item_text === 'function' ? this.item_text(item) : item?.[this.item_text];
            return String(raw ?? '');
        },
        getItemValue(item) {
            if (typeof this.item_value === 'function')
                return this.item_value(item);
            else if (typeof this.item_value === 'string')
                return item[this.item_value];
            else
                return null;
        },
        resolveDisplayText(value) {
            if (value === null || value === undefined)
                return '';
            const match = (this.lookupitems || []).find(a => this.getItemValue(a) == value);
            if (match)
                return this.getItemLabel(match);
            return String(value);
        },
        resolveCommitValue(text) {
            const match = (this.lookupitems || []).find(a => this.getItemLabel(a) == text || this.getItemValue(a) == text);
            return match ? this.getItemValue(match) : text;
        },
        applyCustomInputEdit(val, { fromFilter = false } = {}) {
            if (!this.formatData?.AllowCustomInput || !this.isCustomInputEditing)
                return;
            const next = val ?? '';
            const display = this.resolveDisplayText(this.itemvalue);

            // Quasar focus often @filter's with "" or echoes the current label. Those are not edits.
            // Empty from inputValue/keyup is treated as an intentional clear.
            if (!this.customInputDirty && next === display)
                return;
            if (!this.customInputDirty && next === '' && fromFilter)
                return;

            // fill-input echoes the current label through inputValue (arrow highlight, map-options).
            // Keystrokes also arrive through @filter, including the character that completes that
            // label ("English"), so only the non-filter echo is ignored.
            if (!fromFilter && this.customInputDirty && next === display && this.customInputText !== display)
                return;

            this.customInputDirty = true;
            this.customInputText = next;
        },
        filterFn(val, update) {
            update(() => {
                this.filter = val;
                this.applyCustomInputEdit(val, { fromFilter: true });
            });
        },
        onCustomInputValue(val) {
            this.applyCustomInputEdit(val);
        },
        onCustomInputFocus() {
            this.onFocus();
            if (this.formatData?.AllowCustomInput) {
                this.isCustomInputEditing = true;
                this.customInputText = this.resolveDisplayText(this.itemvalue);
                this.customInputDirty = false;
            }
        },
        commitCustomInput(textOverride) {
            if (!this.formatData?.AllowCustomInput || !this.isCustomInputEditing)
                return;
            const dirty = this.customInputDirty;
            const text = textOverride !== undefined ? textOverride : (this.customInputText ?? '');
            this.isCustomInputEditing = false;
            this.customInputDirty = false;
            this.customInputText = '';
            this.filter = '';
            if (dirty)
                this.setSelectedValue(this.resolveCommitValue(text ?? ''));
        },
        onCustomInputBlur() {
            this.commitCustomInput();
            this.onBlur();
        },
        onCustomInputKeydown(e) {
            if (!this.formatData?.AllowCustomInput || !this.isCustomInputEditing)
                return;
            if (e.key === 'Enter' || e.key === 'Tab')
                this.commitCustomInput(e?.target?.value);
        },
        onCustomInputKeyup(e) {
            // Remember the typed text. Do not sync it; fill-input shows the label, not the value.
            if (!this.formatData?.AllowCustomInput || !this.isCustomInputEditing)
                return;
            this.applyCustomInputEdit(e?.target?.value ?? '');
        },
        setSelectedValue(value) {
            // Drop the in-progress edit first so a later blur cannot replace the option value
            // with the label still sitting in the text box.
            this.isCustomInputEditing = false;
            this.customInputDirty = false;
            this.customInputText = '';
            this.filter = '';
            this.sync(value);
            this.$nextTick(() => {
                const item = (this.lookupitems || []).find(a => this.getItemValue(a) == value);
                const label = item ? this.getItemLabel(item) : (value == null ? '' : String(value));
                this.$refs.selectRef?.updateInputValue?.(label, true);
                this.$refs.selectRef?.hidePopup();
            });
        },
        getItemIcon(item) {
            let icon = null;
            if (typeof this.item_icon === 'function') {
                icon = this.item_icon(item);
            } else if (typeof this.item_icon === 'string') {
                icon = item[this.item_icon];
            }
            if (!icon && this.formatData && this.formatData.DefaultIcon) {
                const name = String(this.formatData.DefaultIcon).replace(/^\s*mdi\s+/, '').trim() || this.formatData.DefaultIcon;
                return <QIcon name={name} size="24px" style={{ width: '38px', minWidth: '38px' }} />;
            }
            if (icon) {
                return <QImg src={icon} width="38px" height="24px" style={{ minWidth: '38px' }} />;
            }
            return null;
        },
        getCurrentItemIcon() {
            const value = this.itemvalue;
            const index = this.lookupitems.findIndex(a => this.getItemValue(a) == value);
            if (index >= 0)
                return this.getItemIcon(this.lookupitems[index]);
            else
                return null;
        },
    },
    render() {
        if (!this.lookupitems)
            return null;

        const variableField = this.tryRenderVariableBindingField({
            class: 'c-formBasicIconPicker caption pa-0 ma-0 mt-1 sform-input-white c-variable-binding-select',
            labelSlot: true,
        });
        if (variableField)
            return variableField;

        let slots = {
            message: ({ message }) => <TranslationContainer context={this} value={message} />,
            ...(this.hintText && appSettings.DebugTranslationPrefixSetting && {
                hint: () => <TranslationContainer context={this} value={this.hintText} />,
            }),
            option: ({ opt, selected, itemProps }) => {
                const item = opt;
                const label = this.getItemLabel(item);
                return (
                    <QItem
                        {...itemProps}
                        active={selected}
                        active-class="text-primary"
                    >
                        <QItemSection avatar>
                            {this.getItemIcon(item)}
                        </QItemSection>
                        <QItemSection>
                            <QItemLabel>
                                <TranslationContainer context={this} value={label} />
                            </QItemLabel>
                        </QItemSection>
                    </QItem>
                );
            },
        };

        let icon = this.getCurrentItemIcon();

        if (icon) {
            slots.prepend = () => icon;
        }
        if (this.hasInputLabel)
            slots.label = () => <TranslationContainer context={this} value={this.labelText} />;

        slots = this.mergeVariableAssistSlots(slots);

        const allowCustom = !!(this.formatData?.AllowCustomInput);

        let input = (
            <QSelect
                ref="selectRef"
                class="c-formBasicIconPicker caption pa-0 ma-0 mt-1 sform-input-white"
                popup-content-class="q-select-max-popup-height"
                outlined
                dense
                labelSlot={this.hasInputLabel}
                style={{ width: '100%' }}
                modelValue={this.itemvalue}
                options={this.filteredItems}
                option-label={typeof this.item_text === 'function' ? this.getItemLabel : this.item_text}
                option-value={this.item_value}
                emit-value
                map-options
                use-input
                fill-input
                hide-selected
                input-debounce={0}
                inputValue={allowCustom && this.isCustomInputEditing ? this.customInputText : undefined}
                onUpdate:inputValue={allowCustom ? this.onCustomInputValue : undefined}
                onFilter={this.filterFn}
                onUpdate:modelValue={(value) => { this.setSelectedValue(value); }}
                onFocus={allowCustom ? () => { this._vbFocused = true; this.onCustomInputFocus(); } : () => { this._vbFocused = true; this.onFocus(); }}
                onBlur={allowCustom ? () => { this._vbFocused = false; this.onCustomInputBlur(); } : () => { this._vbFocused = false; this.onBlur(); }}
                onKeydown={allowCustom ? this.onCustomInputKeydown : undefined}
                onKeyup={allowCustom ? this.onCustomInputKeyup : undefined}
                hint={this.inputHint}
                hide-hint
                bottom-slots={this.showFieldBottomSlots}
                autocomplete="off"
            >
                {slots}
            </QSelect>
        );
        

        if (this.appearance)
            utils.applyVue2ToVue3Props(input, this.appearance);
        //input.componentOptions.propsData = { ...input.componentOptions.propsData, ...this.appearance };

        // if (this.directives && this.directives.some(a => a.name == 'on-enter-default'))
        //     input.props.autofocus = true;

        if (this.directives) {
            var applyDirectives = this.directives.map(d => {
                const dirDef = d.directive || resolveDirective(d.name);

                return [dirDef, d.value, d.arg, d.modifiers];
            });
            input = withDirectives(input, applyDirectives);
        }

        return this.wrapLiteralWithVariableAssist(input);
    }
});